import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  PackagePlus, TrendingDown, ArrowRightLeft, Filter, ChevronLeft, ChevronRight, Eye
} from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch { return dateStr; }
};

const formatNumber = (num) => {
  if (num === undefined || num === null) return '-';
  return new Intl.NumberFormat('ro-RO').format(num);
};

const getMovementIcon = (movement) => {
  if (movement.direction === 'in') return <PackagePlus className="w-4 h-4 text-green-600" />;
  if (movement.direction === 'out') return <TrendingDown className="w-4 h-4 text-red-600" />;
  return <ArrowRightLeft className="w-4 h-4 text-blue-600" />;
};

const getMovementBadge = (movement) => {
  if (movement.direction === 'in') return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">INTRARE</Badge>;
  if (movement.direction === 'out') return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">IESIRE</Badge>;
  if (movement.movement_type === 'transfer') return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">TRANSFER</Badge>;
  if (movement.movement_type === 'adjustment') return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">AJUSTARE</Badge>;
  return <Badge variant="outline">{movement.movement_type?.toUpperCase()}</Badge>;
};

export const MovementsTab = ({
  movements, movementFilter, dateFrom, dateTo,
  onFilterChange, onDateFromChange, onDateToChange, onFetchMovements, onViewDetail
}) => {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg">Istoric Miscari</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={movementFilter} onValueChange={(v) => { onFilterChange(v); onFetchMovements(1); }}>
              <SelectTrigger className="w-40 h-8">
                <Filter className="w-3.5 h-3.5 mr-1" />
                <SelectValue placeholder="Toate" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate</SelectItem>
                <SelectItem value="receipt">Receptii</SelectItem>
                <SelectItem value="quick_receipt">Receptii Rapide</SelectItem>
                <SelectItem value="consumption">Consum</SelectItem>
                <SelectItem value="transfer">Transferuri</SelectItem>
                <SelectItem value="adjustment">Ajustari</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => onDateFromChange(e.target.value)} className="w-36 h-8" />
            <Input type="date" value={dateTo} onChange={(e) => onDateToChange(e.target.value)} className="w-36 h-8" />
            <Button size="sm" variant="outline" onClick={() => onFetchMovements(1)} className="h-8">Filtreaza</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Tip</TableHead>
              <TableHead className="text-right">Cantitate</TableHead>
              <TableHead>Locatie BIN</TableHead>
              <TableHead>Document</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead className="text-center">Actiuni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {movements.items?.map((m, idx) => (
              <TableRow key={m.id || idx} className="hover:bg-slate-50">
                <TableCell className="font-mono text-sm">{formatDate(m.created_at)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getMovementIcon(m)}
                    {getMovementBadge(m)}
                  </div>
                </TableCell>
                <TableCell className={`text-right font-semibold ${m.direction === 'in' ? 'text-green-600' : m.direction === 'out' ? 'text-red-600' : ''}`}>
                  {m.direction === 'in' ? '+' : m.direction === 'out' ? '' : ''}{formatNumber(m.quantity)}
                </TableCell>
                <TableCell>{m.bin_location ? <span className="text-sm text-slate-600">{m.bin_location}</span> : '-'}</TableCell>
                <TableCell>
                  {m.reference_id ? (
                    <span className="text-sm text-slate-600 font-mono">
                      {m.reference_type?.replace(/_/g, ' ').toUpperCase()} #{m.reference_id?.slice(0, 8)}
                    </span>
                  ) : '-'}
                </TableCell>
                <TableCell><span className="text-sm">{m.created_by_name || m.performed_by_name || '-'}</span></TableCell>
                <TableCell className="text-center">
                  {m.reference_id && (
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 px-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                      onClick={() => onViewDetail(m.document_type || m.reference_type, m.reference_id)}
                      data-testid={`view-detail-${m.id}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" /> Vezi
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {movements.items?.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                  Nu exista miscari pentru filtrele selectate
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {movements.total_pages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <span className="text-sm text-slate-500">
              Pagina {movements.page} din {movements.total_pages} ({movements.total} rezultate)
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onFetchMovements(movements.page - 1)} disabled={movements.page <= 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => onFetchMovements(movements.page + 1)} disabled={movements.page >= movements.total_pages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
